import type { I18n } from 'emoji-mart';
import type { DateTime } from 'luxon';
import type { ReactNode } from 'react';

import { Box } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

export const T_4K_STREAMING: string = translate('4K streaming');

export const T_4K_STREAMING_TOOLTIP: string = translate(
  'Once you have used all viewing or streaming hours, an extra fee will be charged',
);

export const T_4K_VIEWING_TIME: string = translate('Viewing time');

export const T_4K_VIEWING_TIME_TOOLTIP: string = translate(
  'Total time viewers spend watching this event in 4K',
);

export const T_4K_STREAMING_TIME: string = translate('Streaming time');

export const T_4K_STREAMING_TIME_TOOLTIP: string = translate(
  'The total duration of 4K content streamed',
);

export const T_4K_ANALYTICS_NOTICE: string = translate(
  'To increase your limits, please reach out to your Account Manager',
);

export const T_4K_ANALYTICS_TIME_RANGE_START = (hoursLabel: string, minutes: number) =>
  translate({
    replacements: {
      HOURS: hoursLabel,
      MINUTES: minutes,
    },
    count: minutes === 0 ? 1 : 2,
    plural: '{HOURS} hr {MINUTES} m',
    singular: '{HOURS} hr',
  });

export const T_4K_ANALYTICS_TIME_RANGE_END = (total: string) =>
  translate({
    replacements: {
      TOTAL: total,
    },
    singular: 'of {TOTAL} hr',
  });

export const T_ADD: string = translate('Add');

export const T_ADD_AUTO_CC_KEYWORD: string = translate('Add keywords');

export const T_ADD_VIDEOS: string = translate('Add videos');

export const T_AUTO: string = translate('Auto');

export const T_AUTO_CC_IS_NOT_AVAILABLE_FOR_DEMO: string = translate(
  'Automatic closed captions are not available for demo events',
);

export const T_AUTO_CC_LOW_LATENCY_STREAM_TIP: string = translate({
  replacements: {
    NO_BREAK: (children: ReactNode | Array<ReactNode>) => (
      <Box as={'span'} whiteSpace={'nowrap'}>
        {children}
      </Box>
    ),
  },
  singular:
    'Automatic closed captions are not supported with {NO_BREAK}low-latency{/NO_BREAK} streaming',
});

export const T_AUTO_CC_PLAN_LIMIT_REACHED: string = translate(
  "You've reached your plan limit for automatic closed captions",
);

export const T_AUTO_CC_REMAINING: string = translate('Total caption time remaining');

export const T_AUTO_CC_TIP: string = translate(
  'If you hit the limit mid-stream, your viewers will still see captions through the remainder of the event',
);

export const T_AUTO_CC_UPSELL: string = translate(
  'Reach a wider audience with unlimited automatic closed captions',
);

export const T_AUTO_CC_UPSELL_REACHED: string = translate(
  'You’ve reached your limit. Reach a wider audience with unlimited automatic closed captions.',
);

export const T_AUTO_CC_ENHANCED_SPEECH: string = translate('Enhanced speech detection');

export const T_AUTOMATICALLY_USED_IF_YOU_PRIMARY_STREAM_FAILS: string = translate(
  'Automatically used if your primary stream fails',
);

export const T_ADD_COMMA_SEPARATED_AUTO_CC_KEYWORDS: string = translate(
  'Press enter to add keywords or separate by commas',
);

export const T_ADD_VIDEO_TO_SIMULATED_LIVE_SCENES: string = translate(
  'Add a video to the prerecorded scenes to go live automatically at the scheduled date',
);

export const T_ADJUST_YOUR_CONFIGURATION: string = translate(
  'Adjust your configuration in your organization’s setup page',
);

export const T_ANALYTICS_RECORDING_NOTICE: string = translate(
  "Analytics are unavailable for events that aren't recorded",
);

export const T_AVG_VIEW_TIME: string = translate('Avg. time watched');

export const T_AVG_VIEW_TIP: string = translate('Average time spent watching per view');

export const T_BACKGROUND_COLOR: string = translate('Background color');

export const T_BACKUP_STREAM_ID = (identificatorName: string) =>
  translate({
    replacements: {
      IDENTIFICATOR: identificatorName,
    },
    singular: 'Backup stream {IDENTIFICATOR}',
  });

export const T_BROWSE_TEMPLATES: string = translate('Browse templates');

export const T_CANCEL: string = translate('Cancel');

export const T_CANT_CONNECT_TO_DEVICES: string = translate(
  "Vimeo can't connect to your camera and microphone because they're already being used by another application. Please close that application to continue.",
);

export const T_CHANGE_SETTINGS_TO_SHARE_SCREEN: string = translate(
  'Change browser settings to share your screen',
);

export const T_CHANGE_SETTINGS_TO_SHARE_SCREEN_DESCRIPTION: string = translate(
  "Your browser isn't enabling screen sharing. Please update your settings and try again.",
);

export const T_CHAT: string = translate('Chat');

export const T_CLOSE: string = translate('Close');

export const T_COMPANY_PAGE: string = translate('Company page');

export const T_CONNECTION_PAUSED: string = translate('Connection paused');

export const T_CONNECT: string = translate('Connect');

export const T_CONTACT_SALES: string = translate('Contact sales');

export const T_CONTACT_US: string = translate('Contact us');

export const T_COPIED_BACKUP_STREAM_ID = (identificatorName: string) =>
  translate({
    replacements: {
      IDENTIFICATOR: identificatorName,
    },
    singular: 'Copied Backup stream {IDENTIFICATOR}',
  });

export const T_COPIED_URL = (encoder: string) =>
  translate({
    replacements: {
      ENCODER: encoder,
    },
    singular: 'Copied {ENCODER} URL',
  });

export const T_COPIED_PASSPHRASE: string = translate('Copied passphrase');

export const T_COPIED_STREAM_ID = (identificatorName: string) =>
  translate({
    replacements: {
      IDENTIFICATOR: identificatorName,
    },
    singular: 'Copied stream {IDENTIFICATOR}',
  });

export const T_CORRECT_RESPONSE = (response: string) =>
  translate({
    replacements: {
      RESPONSE: response,
    },
    singular: 'Correct response: {RESPONSE}',
  });

export const T_CUSTOM: string = translate('Custom');

export const T_DEVICES_ARE_BLOCKED: string = translate('Camera and microphone are blocked');

export const T_DEVICES_IN_USE: string = translate('Camera and mic already in use');

export const T_DVR: string = translate('DVR');

export const T_DVR_TIP: string = translate(
  'Viewers can watch in real time or pause and rewind to catch up during the live broadcast',
);

export const T_DVR_IS_NOT_SUPPORTED_WITH_LOW_LATENCY: string = translate(
  'DVR is not supported with low-latency streaming',
);

export const T_DVR_IS_NOT_SUPPORTED_WITH_UNLIMITED_DURATION: string = translate(
  'Record event is not supported with 12+ hour streaming',
);

export const T_DVR_IS_NOT_SUPPORTED_WITHOUT_ARCHIVE: string = translate(
  'DVR is not supported without recording your event',
);

/** Translations for emoji panel based on predefined fields from the emoji-mart package. */
export const T_EMOJI_PANEL = {
  add_custom: translate('Add custom emoji'),
  categories: {
    activity: translate('Activity'),
    custom: translate('Custom'),
    flags: translate('Flags'),
    foods: translate('Food & Drink'),
    frequent: translate('Frequently used'),
    nature: translate('Animals & Nature'),
    objects: translate('Objects'),
    people: translate('Smileys & People'),
    places: translate('Travel & Places'),
    search: translate('Search Results'),
    symbols: translate('Symbols'),
  },
  pick: translate('Pick an emoji…'),
  search: translate('Search'),
  search_no_results_1: translate('Oh no!'),
  search_no_results_2: translate('That emoji couldn’t be found'),
  skins: {
    1: translate('Default'),
    2: translate('Light'),
    3: translate('Medium-Light'),
    4: translate('Medium'),
    5: translate('Medium-Dark'),
    6: translate('Dark'),
    choose: translate('Choose default skin tone'),
  },
} as I18n;

export const T_ENCRYPTION: string = translate('Encryption');

export const T_ENCRYPTION_PROTECT_STREAM: string = translate(
  'Protect your stream with end-to-end encryption',
);

export const T_EXPORT: string = translate('Export');

export const T_EXPORT_CHAT: string = translate('Export Chat');

export const T_EXPORT_CHAT_30_DAYS: string = translate('Export chat (last 30 days)');

export const T_EXPORT_CSV: string = translate('Export CSV');

export const T_EXPORT_POLLS: string = translate('Export Polls');

export const T_EXPORT_QNA: string = translate('Export Q&A');

export const T_FILE_CANT_BE_UPLOADED = (count: number, fileSizeLimit: number) =>
  translate({
    count,
    plural:
      'Your files can’t be uploaded because they exceed the size limit of {FILE_SIZE_LIMIT}MB',
    replacements: {
      FILE_SIZE_LIMIT: fileSizeLimit,
    },
    singular:
      'Your file can’t be uploaded because it exceeds the size limit of {FILE_SIZE_LIMIT}MB',
  });

export const T_GOT_IT: string = translate('Got it');

export const T_GRADIENT: string = translate('Gradient');

export const T_HELP: string = translate('Help');

export const T_HELP_CENTER_VISIT: string = translate('Visit help center');

export const T_HELP_ME_TROUBLESHOOT: string = translate('Help me troubleshoot');

export const T_INCOMING: string = translate('Incoming');

export const T_INVALID_EVENT_TYPE: string = translate('Invalid event type');

export const T_INVITE_SPEAKER: string = translate('Invite speaker');

export const T_JOIN: string = translate('Join');

export const T_LATENCY: string = translate('Latency');

export const T_LATENCY_TIP: string = translate(
  'Latency measures the delay between you and what your audience sees. Low latency has less delay. High latency has better video quality.',
);

export const T_LEAVE_EVENT: string = translate('Leave Event');

export const T_LINK_COPIED: string = translate('Link copied');

export const T_LINK_SHARE: string = translate('Link Share');

export const T_LINK_SHARE_ONLY_ME: string = translate('Only you and people with access can view');

export const T_LINK_SHARE_PUBLIC: string = translate('Anyone on the internet can view');

export const T_LINK_SHARE_PASSWORD: string = translate('Only people with the password can view');

export const T_LINK_SHARE_UNLISTED: string = translate('Only people with the link can view');

export const T_LINK_SHARE_EMBED_ONLY: string = translate(
  'Private on your account, but embeddable anywhere.',
);

export const T_LINKEDIN: string = translate('LinkedIn');

export const T_LINKEDIN_EXISTING_EVENT_DATE = (dateTime: DateTime) =>
  translate({
    replacements: {
      DATE: () => dateTime.toFormat('D'),
      TIME: () => dateTime.toFormat('t'),
    },
    singular: '{DATE} at {TIME}',
  });

export const T_LINKEDIN_FROM_15_MINUTES_TO_2_HOURS: string = translate(
  'You can go live to LinkedIn 15 minutes before to 2 hours after your scheduled start time',
);

export const T_LINKEDIN_STREAMING_SETTINGS: string = translate('LinkedIn streaming settings');

export const T_LIVE: string = translate('Live');

export const T_MANAGE_VIDEO: string = translate('Manage Video');

export const T_MAX_AUTO_CC_KEYWORDS_COUNT = (count: number) =>
  translate({
    replacements: {
      MAX_COUNT: count,
    },
    singular: 'Limited to {MAX_COUNT} keywords',
  });

export const T_MORE: string = translate('More');

export const T_MUTE: string = translate('Mute');

export const T_MUTED_BY_HOST: string = translate('Muted by host');

export const T_NO_PAGES_ADDED: string = translate('No pages added');

export const T_OCCUPIED_CAMERA_ERROR: string = translate(
  'Camera in use by another application. Close that application to continue.',
);

export const T_OCCUPIED_MICROPHONE_ERROR: string = translate(
  'Microphone in use by another application. Close that application to continue.',
);

export const T_OF: string = translate('of');

export const T_OK: string = translate('Ok');

export const T_ONLY_TEAM_OWNER_CAN_CONNECT_DESTINATIONS: string = translate(
  'Only the Team Owner can connect new destinations',
);

export const T_HAS_ENDED = (eventTitle: string) =>
  translate({
    replacements: {
      EVENT_TITLE: eventTitle,
    },
    singular: '{EVENT_TITLE} has ended',
  });

export const T_HAS_STOPPED_STREAMING = (eventTitle: string) =>
  translate({
    replacements: {
      EVENT_TITLE: eventTitle,
    },
    singular: '{EVENT_TITLE} has stopped streaming',
  });

export const T_PASSPHRASE: string = translate('Passphrase');

export const T_PEAK_VIEWS: string = translate('Peak views');

export const T_PEAK_VIEWS_TIP: string = translate(
  'Highest number of views watching at the same time so far',
);

export const T_PLACE_CONTENT: string = translate('Place content');

export const T_PLEASE_CONTACT_US_TO_UPGRADE_PLAN: string = translate(
  'Please, contact us to upgrade plan',
);

export const T_POLLS: string = translate('Polls');

export const T_PRIVACY: string = translate('Privacy');

export const T_PRIVACY_IS_SET_BY_SHOWCASE: string = translate(
  'Privacy is set by the showcase this event is part of',
);

export const T_PROFILE_PAGE: string = translate('Profile page');

export const T_QNA: string = translate('Q&A');

export const T_PREVIEW: string = translate('Preview');

export const T_RECONNECT: string = translate('Reconnect');

export const T_RECORDING: string = translate('Recording');

export const T_RECORDING_SETTINGS_MANAGED_EXTERNALLY: string = translate(
  'Recording settings managed externally',
);

export const T_REGISTRANTS: string = translate('Registrants');

export const T_RELOAD_PAGE: string = translate('Reload page');

export const T_REMOVE: string = translate('Remove');

export const T_RETURN_TO_EVENT: string = translate('Return to event');

export const T_RTMP: string = translate('RTMP');

export const T_RTMPS: string = translate('RTMPS');

export const T_SAVE: string = translate('Save');

export const T_SCENE: string = translate('Scene');

export const T_SHARE_LINK: string = translate('Share link');

export const T_SOLID: string = translate('Solid');

export const T_SRT: string = translate('SRT');

export const T_START_CAMERA: string = translate('Start camera');

export const T_STAY_MUTED: string = translate('Stay muted');

export const T_STOP_CAMERA: string = translate('Stop camera');

export const T_STREAM_ID = (identificatorName: string) =>
  translate({
    replacements: {
      IDENTIFICATOR: identificatorName,
    },
    singular: 'Stream {IDENTIFICATOR}',
  });

export const T_STREAM_TO: string = translate('Stream to');

export const T_STREAM_URL: string = translate('Stream URL');

export const T_SUPPORT_CHAT: string = translate('Support chat');

export const T_SWITCH_CAMERA: string = translate('Switch camera');

export const T_THE_BROADCASTER_WOULD_LIKE_YOU_TO_UNMUTE: string = translate(
  'The broadcaster would like you to unmute',
);

export const T_THIS_PAGE_SUPPORTS_ONLY: string = translate(
  'This page support only completed Live Event',
);

export const T_TOTAL_VIEWS: string = translate('Total views');

export const T_TOTAL_VIEWS_TIP: string = translate(
  'Number of times the stream has been viewed, including by repeat viewers',
);

export const T_TRACK_VIMEO_EVENTS: string = translate('Track your Vimeo stream views in real time');

export const T_UNABLE_TO_CONNECT_TO_AGORA: string = translate(
  "We're unable to connect to your camera or microphone. Please check your device and network settings and try again.",
);

export const T_UNMUTE: string = translate('Unmute');

export const T_UNMUTE_TO_RECORD_YOUR_VOICE: string = translate('Unmute to record your voice');

export const T_UNMUTE_TO_SPEAK_TO_AUDIENCE: string = translate('Unmute to speak to the audience');

export const T_UNMUTE_TO_SPEAK_TO_BACKSTAGE: string = translate('Unmute to speak backstage');

export const T_UNMUTE_TO_SPEAK_TO_EVERYONE: string = translate('Unmute to speak to everyone');

export const T_UPGRADE: string = translate('Upgrade');

export const T_VIDEO_CARD_PREVIEW_ERROR: string = translate('Video cannot be found');

export const T_VIDEO_CARD_PREVIEW_ERROR_DESCRIPTION: string = translate(
  'The permissions for this video may have been changed. Please replace it with another video.',
);

export const T_VIEW_SETTINGS: string = translate('View settings');

export const T_VIEW_YOUR_ANALYTICS: string = translate({
  singular: 'View analytics when your{LINE_BREAK}event is live',
  replacements: {
    LINE_BREAK: () => <br key={'br'} />,
  },
});

export const T_YOU_ARE_OFF_CAMERA_AND_MUTED: string = translate("You're off camera and muted");

export const T_YOU_CAN_MANAGE_EVENT: string = translate(
  'You can still manage the event here. To be on camera, go to the "Speakers" panel, select a broadcaster, and click "Switch camera."',
);

export const T_YOU_CAN_START_ANOTHER_STREAM: string = translate(
  'You can start another stream by going live again',
);

export const T_YOUR_CAMERA_IS_BLOCKED: string = translate('Your camera is blocked');

export const T_YOUR_MIC_IS_CURRENTLY_MUTED: string = translate('Your mic is currently muted');

export const T_YOU_ARE_LIVE_AND_MUTED: string = translate('You are live and muted');

export const T_YOU_ARE_RECORDING_AND_MUTED: string = translate('You are recording and muted');

export const T_YOUR_MICROPHONE_IS_BLOCKED: string = translate('Your microphone is blocked');

export const T_WATCHING_NOW: string = translate('Watching now');

export const T_WATCHING_NOW_TIP: string = translate('Viewers watching the stream at this moment');

export const T_WE_ARE_UNABLE_TO_CONNECT_TO_YOUR_CAMERA: string = translate(
  "We're unable to connect to your camera. Please check your device and network settings and try again.",
);

export const T_WE_ARE_UNABLE_TO_CONNECT_TO_YOUR_MICROPHONE: string = translate(
  "We're unable to connect to your microphone. Please check your device and network settings and try again.",
);
